import { useRef, useEffect, useMemo, useCallback, useState, type RefObject } from 'react';
import { toast } from 'sonner';
import { onRevalidationChange, isRevalidating } from '@/lib/cache/swr';
import {
  Phone,
  PhoneOff,
  MessageSquare,
  Mail,
  Radio,
  ArrowLeft,
  MoreVertical,
  Loader2,
  Pin,
  PinOff,
  Archive,
  Copy,
  Database,
  MessageCircle,
  ArrowRightLeft,
  Search,
  Bell,
  Pencil,
  Image as ImageIcon,
  FileText,
  Sun,
  ChevronDown,
  ChevronRight,
  ChevronsRight,
  Calendar,
  CalendarClock,
  Clock,
  X,
  Timer,
  TimerOff,
  Trash2,
  Send,
  PhoneOutgoing,
  ShieldCheck,
  Ban,
  AlertTriangle,
  Workflow,
  StopCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhoneNumber } from '@/lib/utils/format';
import { isWaUserId } from '@/lib/utils/phone';
import { isConversationOptedOut, optOutLockKey } from '@/lib/utils/opt-out';
import { Button } from '@/components/ui/button';
import { ConvLabelPicker } from './conv-label-picker';
import { ConvAssigneePicker } from './conv-assignee-picker';
import { ConversationInfoPanel } from './conversation-info-panel';
import { Skeleton } from '@/components/ui/skeleton';
import { ChatMessage, DateSeparator } from './chat-message';
import { ChatInput } from './chat-input';
import { ChatPresenceAvatars } from './chat-presence-avatars';
import { useChatPresence } from '@/hooks/use-chat-presence';
import { useModuleStore } from '@/stores/module-store';
import { useUIStore } from '@/stores/ui-store';
import { getSenderPermissionStandalone, senderPermissionAllowsSend } from '@/hooks/use-permissions';
import { useVisibleCallerIds } from '@/hooks/use-visible-caller-ids';
import { useConversationStore } from '@/stores/conversation-store';
import { useCallStore } from '@/stores/call-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { buildCustomSenders } from '@/hooks/use-senders';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { LOOKUP_SUPPORTED_SAAS } from '@/modules/tpa/lookup/lookup-types';
import { WhatsAppCallSettingsDialog } from '@/components/voice/whatsapp-call-settings-dialog';
import type { Conversation, Message, Channel, Sender } from '@/types/messaging';
import type { ConvertedMessage } from '@/modules/tpa/whatsapp/whatsapp-message-converter';

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_COLORS: Record<Channel, string> = {
  SMS: 'bg-blue-500/10 text-blue-600',
  MMS: 'bg-blue-500/10 text-blue-600',
  WHATSAPP: 'bg-green-500/10 text-green-600',
  VOICE: 'bg-orange-500/10 text-orange-600',
  EMAIL: 'bg-purple-500/10 text-purple-600',
  INTERCOM: 'bg-blue-600/10 text-blue-600',
  INSTAGRAM: 'bg-pink-500/10 text-pink-600',
  TELEGRAM: 'bg-sky-500/10 text-sky-600',
};

const CHANNEL_LABELS: Record<Channel, string> = {
  SMS: 'SMS',
  MMS: 'MMS',
  WHATSAPP: 'WhatsApp',
  VOICE: 'Voice',
  EMAIL: 'Email',
  INTERCOM: 'Intercom',
  INSTAGRAM: 'Instagram',
  TELEGRAM: 'Telegram',
};

/** Action shown in the empty-messages state for CRM chat view */
export interface EmptyStateAction {
  label: string;
  description: string;
  icon: 'sms' | 'whatsapp' | 'search';
  /** Identifier for the action (e.g. channel name or phone field key) */
  key: string;
}

interface ChatThreadProps {
  conversation: Conversation;
  messages: Message[];
  loading: boolean;
  onBack: () => void;
  onRefresh: () => void;
  onMessageSent: () => void;
  /** Hide the chat header (avatar, contact info, actions). Used when from/to selection bar is shown instead. */
  hideHeader?: boolean;
  /** Actions to show when no messages are found (CRM mode). E.g. "Search WhatsApp", "Try Mobile number" */
  emptyActions?: EmptyStateAction[];
  /** Callback when an empty-state action is clicked */
  onEmptyAction?: (action: EmptyStateAction) => void;
  /** Key of the action currently being searched (shows spinner) */
  emptyActionLoading?: string | null;
  /** Key of the action that returned no results (shows tooltip) */
  emptyActionNoResult?: string | null;
  /** Override the "No messages in this conversation" text (e.g. "No WhatsApp messages with this number") */
  emptyMessageOverride?: string;
}

export function ChatThread({
  conversation,
  messages,
  loading,
  onBack,
  onRefresh,
  onMessageSent,
  hideHeader,
  emptyActions,
  onEmptyAction,
  emptyActionLoading,
  emptyActionNoResult,
  emptyMessageOverride,
}: ChatThreadProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showJumpDate, setShowJumpDate] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [blockedOverlayDismissed, setBlockedOverlayDismissed] = useState(false);
  const convId = conversation.id;
  const contactPhone = conversation.contactPhone;
  const isBlocked = conversation.label === 'blocked';

  // Chat Automation flow status for this conversation (set while a flow is paused
  // mid-run). Shown as a pinned banner above the thread; agent can stop it here.
  // `_activeFlow` rides the conversation doc, so it arrives with the normal
  // inbox-details fetch (list load + fetchActiveConvDetails on open) and the
  // Firestore refresh bus — no dedicated listener.
  const activeFlow = conversation.activeFlow;
  const stopActiveFlow = useConversationStore((s) => s.stopActiveFlow);

  // KAN-155: the customer opted out (replied STOP) — the reply box and Retry
  // stay locked until the agent picks "Send anyway". That unlocks this chat
  // only, and only for this opt-out: the key carries the opt-out time, so a
  // newer STOP locks it again. Held here because both of them follow it.
  const optOutSettings = useSettingsStore((s) => s.optOutSettings);
  const [optOutOverrideKey, setOptOutOverrideKey] = useState<string | null>(null);
  const optOutLocked = isConversationOptedOut(conversation, optOutSettings)
    && optOutOverrideKey !== optOutLockKey(conversation);
  useEffect(() => {
    // Leaving the chat re-locks it.
    setOptOutOverrideKey(null);
  }, [conversation.id]);

  // Does the info menu say "View reminder" or "Set reminder"? The conversation
  // badge (__userNextReminder_{uid}) can't answer that on its own: the server
  // clears it when ANY reminder on the chat is closed, even if others are still
  // pending, and leaves it behind when one merely fires. Reading the same
  // drawer the reminder panel renders keeps the label and the panel in step —
  // the badge is only trusted while the drawer is still answering.
  // Ask about THIS chat rather than filtering a user-wide list: one small query
  // per chat, and it cannot be wrong because the chat's reminder happened to sit
  // outside the page the global list had loaded.
  const chatReminders = useConversationStore((s) => s.chatReminderRows);
  const chatRemindersFor = useConversationStore((s) => s.chatReminderConvId);
  const chatRemindersLoading = useConversationStore((s) => s.chatRemindersLoading);
  const chatRemindersFailed = useConversationStore((s) => s.chatRemindersFailed);
  const fetchChatReminders = useConversationStore((s) => s.fetchChatReminders);
  useEffect(() => { fetchChatReminders(conversation.id); }, [fetchChatReminders, conversation.id]);
  const hasReminderOnThisChat = useMemo(() => {
    if (chatRemindersFor === conversation.id && chatReminders.length > 0) return true;
    // An empty result only means "no reminders" once it has actually answered.
    // While it is loading — or after the read failed, e.g. Firestore rules — fall
    // back to the badge, or the menu would offer "Set reminder" on a chat that
    // already has one and the agent would file a duplicate.
    const answered = chatRemindersFor === conversation.id && !chatRemindersLoading && !chatRemindersFailed;
    return !answered && !!conversation.metadata?.hasReminder;
  }, [chatReminders, chatRemindersFor, chatRemindersLoading, chatRemindersFailed, conversation.id, conversation.metadata?.hasReminder]);
  const [stoppingFlow, setStoppingFlow] = useState(false);
  const handleStopFlow = useCallback(async () => {
    if (stoppingFlow) return;
    setStoppingFlow(true);
    try {
      await stopActiveFlow(convId);
    } finally {
      setStoppingFlow(false);
    }
  }, [stoppingFlow, stopActiveFlow, convId]);

  // Track SWR revalidation for messages — matches both messages:{convId} and contactSearch:{phone}
  const matchesRevalidation = useCallback((keys: Set<string>) => {
    for (const k of keys) {
      if (k.startsWith('messages:') && k.includes(convId)) return true;
      if (k.startsWith('contactSearch:') && contactPhone && k.includes(contactPhone)) return true;
      if (k.startsWith('conversations:') && contactPhone && k.includes(contactPhone)) return true;
    }
    return false;
  }, [convId, contactPhone]);

  const [revalidating, setRevalidating] = useState(() => {
    // Check all current revalidating keys on mount
    return isRevalidating(convId) || (contactPhone ? isRevalidating(contactPhone) : false);
  });

  useEffect(() => {
    // Re-check on conversation change
    setRevalidating(isRevalidating(convId) || (contactPhone ? isRevalidating(contactPhone) : false));
    return onRevalidationChange((keys) => setRevalidating(matchesRevalidation(keys)));
  }, [convId, contactPhone, matchesRevalidation]);

  // Clear reply and reset blocked overlay when conversation changes
  useEffect(() => {
    setReplyingTo(null);
    setBlockedOverlayDismissed(false);
  }, [conversation.id]);


  // Track message IDs added via realtime events — only these get the new-message animation
  const realtimeMessageIds = useConversationStore((s) => s.realtimeMessageIds);
  const clearRealtimeMessageIds = useConversationStore((s) => s.clearRealtimeMessageIds);

  // Track scroll height before older messages are prepended so we can restore position
  const prevScrollHeightRef = useRef(0);
  const isLoadingOlderRef = useRef(false);

  const [stickyDate, setStickyDate] = useState<string | null>(null);
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const isPlaceholder = conversation.contactName.startsWith('Start ') && conversation.contactName.endsWith(' conversation');
  // User collision: who else has this chat open / is typing. Placeholder rows
  // ("Start SMS conversation") already carry the deterministic `sender::contact`
  // id the real conversation will get, so two agents about to open the same
  // new chat still see each other.
  const { others: presenceOthers, setTyping: setPresenceTyping } = useChatPresence(convId);

  const togglePin = useConversationStore((s) => s.togglePin);
  const pinMessage = useConversationStore((s) => s.pinMessage);
  const setPinnedMessage = useConversationStore((s) => s.setPinnedMessage);
  const loadOlderMessages = useConversationStore((s) => s.loadOlderMessages);
  const loadingOlderMessages = useConversationStore((s) => s.loadingOlderMessages);
  const messagesHasMore = useConversationStore((s) => s.messagesHasMore);
  const jumpToMessagesAtDate = useConversationStore((s) => s.jumpToMessagesAtDate);
  const cancelScheduledMessage = useConversationStore((s) => s.cancelScheduledMessage);
  const starMessage = useConversationStore((s) => s.starMessage);
  const starredMessages = useConversationStore((s) => s.starredMessages);
  const updateConvSubject = useConversationStore((s) => s.updateConvSubject);
  const markAsRead = useConversationStore((s) => s.markAsRead);

  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectInput, setSubjectInput] = useState('');
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const [showReminderForm, setShowReminderForm] = useState(false);

  // Reset unread count when conversation is opened. `markAsRead`
  // internally fires both the local store update and the server CF
  // (UA_INBOX_UPDATE_CONVERSATION_READ) when there was real unread
  // state, so we don't need to call the CF here separately.
  useEffect(() => {
    if (!conversation.id || conversation.unreadCount <= 0) return;
    markAsRead(conversation.id);
  }, [conversation.id, markAsRead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // Helper: reliably scroll to bottom after DOM paint
  const isEmbedded = useRef((() => { try { return window.self !== window.top; } catch { return true; } })());

  const scrollToBottom = useCallback((instant = true) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (isEmbedded.current) {
      // Use scrollTop/scrollTo — scrollIntoView propagates to parent iframe and causes jumps
      if (instant) {
        container.scrollTop = container.scrollHeight;
      } else {
        requestAnimationFrame(() => {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        });
      }
    } else {
      if (instant) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      } else {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      }
    }
  }, []);

  // Track whether user has manually scrolled away from bottom
  const userScrolledRef = useRef(false);

  // Reset + instant scroll when conversation changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    userScrolledRef.current = false;
    prevMessageCountRef.current = 0;
    scrollToBottom(true);
  }, [conversation.id, scrollToBottom]);

  // Scroll after messages finish loading (loading → false transition)
  // Also mark conversation as read on the server
  const tpaModuleForReadReceipt = useModuleStore((s) => s.tpaModule);

  useEffect(() => {
    if (!loading && messages.length > 0 && isInitialLoadRef.current) {
      scrollToBottom(true);
      isInitialLoadRef.current = false;

      // Server-side mark-as-read happens in the conv-open effect above
      // (combined with the local markAsRead so they share a single
      // unreadCount > 0 gate). This block now only handles provider-
      // level read receipts (WhatsApp blue ticks).

      // Send read receipt to provider (e.g. WhatsApp blue ticks) for last incoming message
      // Skip call events — they don't need read receipts
      if (tpaModuleForReadReceipt?.markMessageAsRead) {
        const lastIncoming = [...messages].reverse().find((m) => m.direction === 'inbound' && !m.metadata?.isCallEvent);
        if (lastIncoming) {
          tpaModuleForReadReceipt.markMessageAsRead(lastIncoming).catch(() => {});
        }
      }
    }
  }, [loading, messages.length, scrollToBottom, conversation.id, messages, tpaModuleForReadReceipt]);

  // Re-scroll when messages array reference changes (e.g. media resolution)
  // but only if user hasn't scrolled away from bottom
  useEffect(() => {
    if (messages.length === 0 || isInitialLoadRef.current) return;
    // Skip if older messages were just prepended — handled separately
    if (isLoadingOlderRef.current) return;
    // Same length but different reference = media resolved or status update
    if (messages.length === prevMessageCountRef.current && !userScrolledRef.current) {
      scrollToBottom(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, scrollToBottom]);

  // Capture scroll position right before older messages load starts
  useEffect(() => {
    if (loadingOlderMessages) {
      const container = scrollContainerRef.current;
      if (container) {
        prevScrollHeightRef.current = container.scrollHeight;
      }
      isLoadingOlderRef.current = true;
    }
  }, [loadingOlderMessages]);

  // Restore scroll position after older messages are prepended
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || messages.length === 0) return;
    if (!isLoadingOlderRef.current) return;
    if (loadingOlderMessages) return; // Wait until loading is done

    // Older messages finished loading and were prepended
    isLoadingOlderRef.current = false;
    const newScrollHeight = container.scrollHeight;
    const heightDiff = newScrollHeight - prevScrollHeightRef.current;
    if (heightDiff > 0) {
      container.scrollTop = heightDiff;
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, loadingOlderMessages]);

  // Smart scroll on new message arrival (bottom messages only, not older prepended)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || messages.length === 0) return;

    const isNewMessage = messages.length > prevMessageCountRef.current && prevMessageCountRef.current > 0;

    if (isNewMessage && !isLoadingOlderRef.current) {
      // Regular new message (appended at bottom)
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
      const latestMsg = messages[messages.length - 1];
      const isOwnMessage = latestMsg?.direction === 'outbound';
      if (isNearBottom || isOwnMessage) {
        userScrolledRef.current = false;
        scrollToBottom(false);
      }
    }

    prevMessageCountRef.current = messages.length;
  }, [messages.length, messages, scrollToBottom]);

  // Track user scroll to avoid fighting with manual scrolling + load older on scroll to top
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      userScrolledRef.current = scrollHeight - scrollTop - clientHeight > 150;

      // Load older messages when scrolling near the top
      if (scrollTop < 80 && messagesHasMore && !loadingOlderMessages) {
        loadOlderMessages();
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messagesHasMore, loadingOlderMessages, loadOlderMessages]);

  // Group messages by date (must be before sticky-date observers)
  // Also aggregate reaction messages into the parent message's reactions array
  const groupedMessages = useMemo(() => {
    const groups: Array<{ date: string; messages: Message[] }> = [];
    let currentDate = '';

    const sorted = [...messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Aggregate reactions: collect reaction messages and attach to parent.
    // WhatsApp only keeps the latest reaction per sender — later reactions replace earlier ones.
    // An empty emoji means the reaction was removed.
    const reactionsByParent = new Map<string, Map<string, { emoji: string; from: string; timestamp: Date }>>();
    const reactionMessageIds = new Set<string>();

    for (const msg of sorted) {
      const cm = msg as ConvertedMessage;
      if (cm.isReactionMessage && cm.replyToMessageId) {
        reactionMessageIds.add(msg.id);
        if (!reactionsByParent.has(cm.replyToMessageId)) {
          reactionsByParent.set(cm.replyToMessageId, new Map());
        }
        const perSender = reactionsByParent.get(cm.replyToMessageId)!;
        const existing = perSender.get(msg.from);
        // Only keep the latest reaction per sender
        if (!existing || msg.timestamp >= existing.timestamp) {
          if (cm.reactionText) {
            perSender.set(msg.from, { emoji: cm.reactionText, from: msg.from, timestamp: msg.timestamp });
          } else {
            // Empty emoji = reaction removed
            perSender.delete(msg.from);
          }
        }
      }
    }

    // Attach reactions to parent messages and filter out reaction messages
    const enriched = sorted
      .filter((m) => !reactionMessageIds.has(m.id))
      .map((m) => {
        const perSender = reactionsByParent.get(m.id);
        const reactions = perSender ? Array.from(perSender.values()) : undefined;
        return reactions && reactions.length > 0 ? { ...m, reactions } : m;
      });

    const currentYear = new Date().getFullYear();

    for (const msg of enriched) {
      const d = new Date(msg.timestamp);
      const opts: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        ...(d.getFullYear() !== currentYear && { year: 'numeric' }),
      };
      const msgDate = d.toLocaleDateString('en-US', opts);

      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: msgDate, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    }

    return groups;
  }, [messages]);

  // Sticky date: observe each date group with IntersectionObserver
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || groupedMessages.length === 0) {
      setStickyDate(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible group by checking which group elements
        // are intersecting and picking the one closest to the top of the container
        const refs = groupRefs.current;
        let topDate: string | null = null;
        let topOffset = Infinity;

        for (const [date, el] of refs) {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          // The group is visible if its bottom is below the container top
          // We want the group whose top is closest to (but at or above) the container top
          const offset = rect.top - containerRect.top;
          if (rect.bottom > containerRect.top && offset < topOffset) {
            topOffset = offset;
            topDate = date;
          }
        }
        if (topDate) setStickyDate(topDate);
      },
      { root: container, threshold: 0 }
    );

    for (const el of groupRefs.current.values()) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [groupedMessages]);

  // Also update sticky date on scroll for smoother transitions
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScrollDate = () => {
      const containerRect = container.getBoundingClientRect();
      let topDate: string | null = null;
      let topOffset = Infinity;
      for (const [date, el] of groupRefs.current) {
        const rect = el.getBoundingClientRect();
        const offset = rect.top - containerRect.top;
        if (rect.bottom > containerRect.top && offset < topOffset) {
          topOffset = offset;
          topDate = date;
        }
      }
      if (topDate) setStickyDate(topDate);
    };
    container.addEventListener('scroll', handleScrollDate, { passive: true });
    return () => container.removeEventListener('scroll', handleScrollDate);
  }, [groupedMessages]);

  // Scroll to bottom when content height changes (images/videos load)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      if (!userScrolledRef.current) {
        if (isEmbedded.current) {
          container.scrollTop = container.scrollHeight;
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
        }
      }
    });
    // Observe children so image/video loads trigger resize
    for (const child of container.children) {
      observer.observe(child);
    }
    return () => observer.disconnect();
  }, [messages]); // re-attach when messages change

  // Retry failed messages
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const fetchMsgs = useConversationStore((s) => s.fetchMessages);
  const handleRetry = useCallback(
    async (failedMsg: Message) => {
      if (!tpaModule) return;
      // KAN-155: Retry sends to the customer too, so it follows the same lock
      // as the reply box.
      if (optOutLocked) {
        toast.error('This customer opted out. Choose "Send anyway" in the reply box first.');
        return;
      }
      // Re-mark as queued IMMEDIATELY — the Retry button only renders while the
      // message is 'failed', so this synchronous flip is also the double-click
      // guard. If the permission gate below blocks, the status is put back.
      const setStatus = (status: 'queued' | 'failed') =>
        useConversationStore.setState({
          messages: useConversationStore
            .getState()
            .messages.map((m) =>
              m.id === failedMsg.id ? { ...m, status } : m
            ),
        });
      setStatus('queued');
      // Resolve the conversation's own sender for the retry — WhatsApp uses
      // `from.id` as the Meta phone_number_id in the send URL, so the old
      // `{ id: 'retry' }` stub could never succeed there. Match by the failed
      // message's `from` number, falling back to the conversation id's
      // sender half (conv id = senderNumber::contactNumber).
      let retryFrom: Sender = {
        id: 'retry',
        label: 'retry',
        channel: conversation.channel,
        phoneNumber: failedMsg.from,
        data: {},
      };
      let senderLookupFailed = false;
      try {
        const fromRaw = (failedMsg.from || conversation.id.split('::')[0] || '').trim();
        const fromDigits = fromRaw.replace(/\D/g, '');
        if (!fromRaw) {
          senderLookupFailed = true;
        } else {
          const apiSenders = await tpaModule.getSenders();
          const inChannel = (s: Sender) =>
            !conversation.channel || !s.channel || s.channel === conversation.channel;
          // A failed lookup is indistinguishable from an empty one unless we
          // look: proxyRequest returns {success:false} rather than throwing and
          // getSenders swallows that, per channel, each with its own catch. So
          // no sender at all for this conversation's channel means the fetch
          // failed — "couldn't check" — not "that number isn't one of ours".
          // Judge that on the API result alone; custom senders come from
          // preferences and would mask an empty fetch.
          if (apiSenders.filter(inChannel).length === 0) senderLookupFailed = true;
          // Alphanumeric senders ("ULGEBRA") are stored in preferences, not
          // returned by getSenders(), so a digits-only match could never
          // identify one and the gate below was skipped entirely. (KAN-14)
          const candidates = [
            ...apiSenders,
            ...buildCustomSenders(useSettingsStore.getState().preferences.customSenders),
          ].filter(inChannel);
          const matched = candidates.find((s) => {
            if (fromDigits) {
              const sDigits = (s.phoneNumber || '').replace(/\D/g, '');
              const sMsisdn = (s.data?.msisdn as string) || '';
              return sDigits === fromDigits || sMsisdn === fromDigits;
            }
            // No digits to compare — match the sender id as written.
            const raw = fromRaw.toLowerCase();
            return (s.phoneNumber || '').trim().toLowerCase() === raw
              || (s.label || '').trim().toLowerCase() === raw;
          });
          if (matched) retryFrom = matched;
          // An alphanumeric sender we could not place is unidentifiable, so the
          // permission keyed to it cannot be read: that is "couldn't check",
          // not "no opinion". Numeric senders keep the older, deliberate
          // allow-on-no-match, which avoided false blocks.
          else if (!fromDigits) senderLookupFailed = true;
        }
      } catch { senderLookupFailed = true; } // a throw counts too, though the API layer rarely throws
      // Permission gate — keyed ONLY on the sender the retry actually delivers
      // from. When the lookup SUCCEEDED but matched no real sender we allow:
      // gating on the composer's unrelated selection produced false blocks, and
      // permissions are keyed by sender id, which a stub does not have. A lookup
      // that FAILED is a different thing — a transient network error must not
      // hand a restricted user a send, so that case fails closed. Admins are
      // never gated, so they retry normally either way. (KAN-14)
      const isSharedUser = !!useAuthStore.getState().selectedAdminUserId;
      if (senderLookupFailed && isSharedUser) {
        setStatus('failed');
        toast.error("Couldn't check your permission for this number. Try again.");
        return;
      }
      if (retryFrom.id !== 'retry'
          && !senderPermissionAllowsSend(getSenderPermissionStandalone(retryFrom.id))) {
        setStatus('failed');
        toast.error("You don't have permission to send from this number");
        return;
      }
      try {
        const result = await tpaModule.sendMessage({
          from: retryFrom,
          to: [
            {
              id: conversation.id,
              name: conversation.contactName,
              phone: conversation.contactPhone,
              source: 'manual',
            },
          ],
          body: failedMsg.body,
          channel: conversation.channel,
          mediaUrl: failedMsg.mediaUrl,
        });
        useConversationStore.setState({
          messages: useConversationStore
            .getState()
            .messages.map((m) =>
              m.id === failedMsg.id
                ? {
                    ...m,
                    id: result.messageId || m.id,
                    status: result.success ? ('sent' as const) : ('failed' as const),
                    metadata: result.success ? m.metadata : { ...m.metadata, errorMessage: result.error },
                  }
                : m
            ),
        });
        if (result.success) {
          setTimeout(() => fetchMsgs(conversation.id), 2500);
        }
      } catch {
        useConversationStore.setState({
          messages: useConversationStore
            .getState()
            .messages.map((m) =>
              m.id === failedMsg.id ? { ...m, status: 'failed' as const } : m
            ),
        });
      }
    },
    [tpaModule, conversation, fetchMsgs, optOutLocked]
  );

  // Pin handler: local UI + Firebase API
  const handlePin = useCallback(
    (messageId: string) => {
      const isPinToggle = conversation.pinnedMessageId === messageId;
      const newPinnedId = isPinToggle ? null : messageId;
      pinMessage(conversation.id, newPinnedId);

      const contactId = conversation.contactPhone?.replace(/\D/g, '') || '';
      if (newPinnedId) {
        const msg = messages.find((m) => m.id === messageId);
        setPinnedMessage(conversation.id, contactId, msg ? { id: msg.id, text: msg.body, createdTime: msg.timestamp.getTime() } : { id: messageId });
      } else {
        setPinnedMessage(conversation.id, contactId, null);
      }
    },
    [conversation.id, conversation.pinnedMessageId, conversation.contactPhone, messages, pinMessage, setPinnedMessage]
  );

  // Star handler
  const handleStar = useCallback(
    (messageId: string) => {
      const isCurrentlyStarred = !!starredMessages[messageId];
      const contactId = conversation.contactPhone?.replace(/\D/g, '') || '';
      starMessage(conversation.id, contactId, messageId, !isCurrentlyStarred);
    },
    [conversation.id, conversation.contactPhone, starredMessages, starMessage]
  );

  // React with emoji handler — toggles: same emoji again sends empty string to remove reaction
  const handleReact = useCallback(
    async (messageId: string, emoji: string) => {
      if (!tpaModule || !conversation) return;
      const activeSender = useConversationStore.getState().activeSender;
      if (!activeSender) return;
      // A reaction is an outbound provider call the customer sees, so it needs
      // the same permission as sending a message. Without this a 'view_only'
      // user could still react from a number they may not send from (KAN-14).
      // activeSender is what the reaction is actually sent from (below), so gate
      // on it directly.
      if (!senderPermissionAllowsSend(getSenderPermissionStandalone(activeSender.id))) return;
      const recipientPhone = conversation.contactPhone?.replace(/\D/g, '') || '';
      if (!recipientPhone) return;
      // Check if user already reacted with this emoji — toggle off by sending empty string
      const msg = messages.find((m) => m.id === messageId);
      const senderNumber = activeSender.phoneNumber?.replace(/\D/g, '') || activeSender.id;
      const existingReaction = msg?.reactions?.find((r) => r.from === senderNumber || r.from === activeSender.id);
      const sendEmoji = existingReaction?.emoji === emoji ? '' : emoji;
      await tpaModule.sendReaction(activeSender.id, recipientPhone, messageId, sendEmoji);
    },
    [tpaModule, conversation, messages]
  );

  // Extract sender & contact numbers from conversation ID (format: senderNumber::contactNumber)
  const idParts = conversation.id?.includes('::') ? conversation.id.split('::') : [];
  const senderNumber = idParts[0]?.replace('whatsapp:', '') || '';
  const contactNumber = idParts[1]?.replace('whatsapp:', '') || '';

  // Resolve display name: same logic as conversation-item.tsx
  const hasRealSubject = conversation.contactName &&
    conversation.contactName !== conversation.contactPhone &&
    conversation.contactName !== `+${conversation.contactPhone}`;
  const displayName = (hasRealSubject ? conversation.contactName : null) || conversation.crmRecordName || conversation.contactName || conversation.contactPhone || 'Unknown';

  // Subject editing
  const handleSubjectEdit = useCallback(() => {
    setSubjectInput(displayName);
    setEditingSubject(true);
    setTimeout(() => subjectInputRef.current?.focus(), 50);
  }, [displayName]);

  const handleSubjectSave = useCallback(() => {
    const trimmed = subjectInput.trim();
    if (trimmed && trimmed !== conversation.contactName) {
      updateConvSubject(conversation.id, trimmed);
    }
    setEditingSubject(false);
  }, [subjectInput, conversation.contactName, conversation.id, updateConvSubject]);

  const Icon = CHANNEL_ICONS[conversation.channel] || Phone;
  const channelColor = CHANNEL_COLORS[conversation.channel] || '';
  const initial = displayName.charAt(0).toUpperCase();

  // Mirror the conversation-list "not on WhatsApp" treatment in the header so
  // both surfaces tell the same story at a glance (error 131026 = recipient
  // not on WhatsApp / has blocked the business).
  const headerNotOnWhatsApp = conversation.channel === 'WHATSAPP'
    && conversation.metadata?.lastMessageStatus === 'failed'
    && (conversation.metadata?.lastFailedCode as number | undefined) === 131026;

  // WhatsApp username user: the chat is keyed by a Meta user ID ("US.1349…")
  // because the customer's phone number is hidden.
  const isUsernameUser = conversation.channel === 'WHATSAPP' && isWaUserId(conversation.contactPhone);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden relative">
      {/* Chat header — hidden when hideHeader is true (e.g. CRM context bar replaces it) */}
      {!hideHeader && (
        <div className={cn(
          "flex items-center border-b border-border bg-card shadow-sm shrink-0",
          crmRecordContext ? "gap-2 px-3 py-1.5" : "gap-3 px-4 py-3"
        )}>
          {/* Back button (mobile) */}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onBack}
            className="md:hidden text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {/* Avatar + channel icon — in CRM context, show only the channel icon */}
          {crmRecordContext ? (
            headerNotOnWhatsApp ? (
              <span className="relative inline-flex h-4 w-4">
                <Icon className={cn('h-4 w-4 opacity-60', channelColor.split(' ')[1])} />
                <Ban className="absolute inset-0 h-4 w-4 text-red-500" strokeWidth={2.5} />
              </span>
            ) : (
              <Icon className={cn('h-4 w-4', channelColor.split(' ')[1])} />
            )
          ) : (
            <div className="relative">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary',
                  headerNotOnWhatsApp && 'opacity-60 grayscale'
                )}
                title={headerNotOnWhatsApp ? 'This number may not be on WhatsApp or has blocked your business' : undefined}
              >
                {headerNotOnWhatsApp ? '?' : initial}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-card border border-border">
                {headerNotOnWhatsApp ? (
                  <span className="relative inline-flex h-2.5 w-2.5">
                    <Icon className={cn('h-2.5 w-2.5 opacity-60', channelColor.split(' ')[1])} />
                    <Ban className="absolute inset-0 h-2.5 w-2.5 text-red-500" strokeWidth={2.5} />
                  </span>
                ) : (
                  <Icon className={cn('h-2.5 w-2.5', channelColor.split(' ')[1])} />
                )}
              </div>
            </div>
          )}

          {/* Contact info */}
          <div className="flex-1 min-w-0">
            {!crmRecordContext && (
              editingSubject ? (
                <input
                  ref={subjectInputRef}
                  value={subjectInput}
                  onChange={(e) => setSubjectInput(e.target.value)}
                  onBlur={handleSubjectSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubjectSave();
                    if (e.key === 'Escape') setEditingSubject(false);
                  }}
                  className="text-sm font-bold text-foreground bg-transparent border-b border-primary outline-none w-full"
                />
              ) : (
                <p
                  data-testid="chat-contact-number"
                  className="text-sm font-bold text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                  onDoubleClick={handleSubjectEdit}
                  title="Double-click to edit name"
                >
                  {displayName}
                </p>
              )
            )}
            <div className={cn("flex items-center gap-1.5", !crmRecordContext && "mt-0.5")}>
              {isUsernameUser && (
                <span
                  className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                  title="This customer uses a WhatsApp username and has not shared a phone number"
                >
                  Username user
                </span>
              )}
              {/* Sender ⇌ contact number line is noise in the standalone Inbox
                  (the contact is already named above, and inbox users don't
                  need to see the routing number). Only useful inside a CRM
                  widget context where the sender number disambiguates multi-
                  number setups. */}
              {/* For phone channels: show "from-phone ⇌ to-phone".
                  For INTERCOM: contactPhone is an opaque contact id. Show the
                  Intercom-side contact email/name (carried through metadata)
                  instead, and skip the sender phone (Intercom admins have no
                  phone). */}
              {crmRecordContext && conversation.channel === 'INTERCOM' && (
                (conversation.metadata?.intercomContactEmail || conversation.metadata?.intercomContactName) && (
                  <p className="text-xs text-muted-foreground truncate">
                    {(conversation.metadata?.intercomContactEmail as string | undefined)
                      || (conversation.metadata?.intercomContactName as string | undefined)}
                  </p>
                )
              )}
              {crmRecordContext && conversation.channel !== 'INTERCOM' && senderNumber && (
                <p className="text-xs text-muted-foreground truncate">
                  {senderNumber} ⇌ {contactNumber || conversation.contactPhone}
                </p>
              )}
              {!crmRecordContext && !isPlaceholder && <ConvLabelPicker conversationId={conversation.id} currentLabel={conversation.label} />}
              {!crmRecordContext && !isPlaceholder && <ConvAssigneePicker conversationId={conversation.id} currentAssignee={conversation.assigneeEmail} botActive={!!conversation.activeFlow} botLabel={conversation.activeFlow?.flowName} />}
              {/* Shown in both Inbox and CRM widget: a colleague may be replying from the other surface. */}
              <ChatPresenceAvatars users={presenceOthers} className="ml-1" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* Call/WhatsApp-call buttons assume contactPhone is dialable.
                For non-phone channels (INTERCOM) the field carries an opaque
                contact id, not a phone — hide them. */}
            {conversation.channel !== 'INTERCOM' && (
              <>
                <CallButton phone={conversation.contactPhone} channel={conversation.channel} />
                <WhatsAppNativeCallButton phone={conversation.contactPhone} channel={conversation.channel} senderMsisdn={senderNumber} />
              </>
            )}
            <LookupToggleButton />
            {!isPlaceholder && (
              <Button
                data-testid="chat-more-menu"
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground"
                onClick={() => setShowMenu(true)}
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Right-side info panel (replaces the old dropdown menu) */}
      <ConversationInfoPanel
        open={showMenu}
        onClose={() => setShowMenu(false)}
        conversation={conversation}
        senderNumber={senderNumber}
        contactNumber={contactNumber}
        hasReminder={hasReminderOnThisChat}
        onCopyPhone={() => {
          const rawPhone = conversation.contactPhone || '';
          const isDigits = /^\d+$/.test(rawPhone);
          const phoneToCopy = isDigits ? `+${rawPhone}` : rawPhone;
          navigator.clipboard.writeText(phoneToCopy);
          setShowMenu(false);
        }}
        onTogglePin={() => { togglePin(conversation.id); setShowMenu(false); }}
        onRename={() => { handleSubjectEdit(); setShowMenu(false); }}
        onJumpToDate={() => { setShowJumpDate(true); setShowMenu(false); }}
        onReminder={() => { setShowReminderForm(true); setShowMenu(false); }}
        onCloseConversation={() => { setShowMenu(false); onBack(); }}
      />

      {/* Inline reminder form */}
      {showReminderForm && (
        <SetReminderForm
          conversationId={conversation.id}
          contactPhone={conversation.contactPhone}
          conversation={conversation}
          onClose={() => setShowReminderForm(false)}
        />
      )}

      {/* Jump to date */}
      {showJumpDate && (
        <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-4 py-2">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground shrink-0">Jump to messages on or before:</span>
          <input
            type="date"
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => {
              if (!e.target.value) return;
              const [y, m, d] = e.target.value.split('-').map(Number);
              // Use end-of-day so messages sent throughout that day are included
              const target = new Date(y, m - 1, d, 23, 59, 59, 999);
              jumpToMessagesAtDate(conversation.id, target);
              setShowJumpDate(false);
            }}
            className="flex-1 max-w-[180px] rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            autoFocus
          />
          <button
            onClick={() => setShowJumpDate(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Pinned message banner */}
      {conversation.pinnedMessageId && (
        <PinnedMessageBanner
          conversation={conversation}
          messages={messages}
          scrollContainerRef={scrollContainerRef}
          onUnpin={() => {
            handlePin(conversation.pinnedMessageId!);
          }}
        />
      )}

      {/* Active-flow banner — Chat Automation is driving this conversation */}
      {activeFlow && (
        <div className="flex items-center gap-2 border-b border-violet-200/60 dark:border-violet-500/20 bg-violet-50/70 dark:bg-violet-500/10 px-4 py-2">
          <Workflow className="h-4 w-4 shrink-0 text-violet-500" />
          <div className="flex-1 min-w-0 text-xs">
            <span className="text-muted-foreground">In flow </span>
            <span className="font-medium text-foreground">{activeFlow.flowName}</span>
            {activeFlow.stage ? (
              <span className="text-muted-foreground"> · {activeFlow.stage}</span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleStopFlow}
            disabled={stoppingFlow}
            className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/5 px-2.5 py-1 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-60"
          >
            {stoppingFlow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <StopCircle className="h-3.5 w-3.5" />}
            <span>Stop flow</span>
          </button>
        </div>
      )}

      {/* Messages area */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {/* Blocked contact overlay */}
        {isBlocked && !blockedOverlayDismissed && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 text-center px-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-slate-200 dark:bg-slate-800">
                <Ban className="h-6 w-6 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-sm text-muted-foreground">This contact is blocked, Do you want to view messages?</p>
              <button
                onClick={() => setBlockedOverlayDismissed(true)}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Show Messages
              </button>
            </div>
          </div>
        )}

        {/* Sticky date label */}
        {stickyDate && messages.length > 0 && (
          <div className="absolute top-0 left-0 right-0 z-10 flex justify-center pointer-events-none pt-2">
            <span className="rounded-full bg-muted/90 backdrop-blur-sm border border-border/50 px-3 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
              {stickyDate}
            </span>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          data-testid="chat-message-thread"
          className={cn(
            'h-full overflow-y-auto overflow-x-hidden px-4 py-4 space-y-1 scrollbar-thin',
            conversation.channel === 'WHATSAPP' ? 'chat-bg-whatsapp' : 'chat-bg-pattern'
          )}
          onClick={() => {
            // Chat-view rule: the trigger for mark-as-read here is the
            // bubble dot — `realtimeMessageIds` is what drives both the
            // dot and this gate. Without realtime arrivals there's
            // nothing the user has freshly engaged with.
            if (realtimeMessageIds.size > 0) {
              markAsRead(conversation.id);
              clearRealtimeMessageIds();
            }
          }}
        >
          {/* Loading older messages indicator */}
          {loadingOlderMessages && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs text-muted-foreground">Loading older messages...</span>
            </div>
          )}

          <div className="min-h-full flex flex-col justify-end">
          {loading && messages.length === 0 ? (
            <MessagesSkeleton />
          ) : messages.length === 0 ? (
            <div data-testid="chat-no-messages" className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageCircle className="h-12 w-12 mb-3 opacity-20" />
              <p className="text-sm">{emptyMessageOverride || 'No messages in this conversation'}</p>
              <p className="text-xs mt-1">Send a message to get started</p>
              {/* Phone-specific tip — hide for non-phone channels (INTERCOM)
                  where contactPhone carries an opaque contact id, not a phone. */}
              {conversation.channel !== 'INTERCOM' && conversation.contactPhone && !/^\+/.test(conversation.contactPhone) && (
                <p data-testid="chat-cc-tip" className="text-xs mt-2 text-amber-600 dark:text-amber-400">
                  Tip: Ensure contact number includes country code (e.g. +1, +21)
                </p>
              )}

              {/* CRM empty-state actions: search other channel / other phone */}
              {emptyActions && emptyActions.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-5 w-full max-w-[260px]">
                  {emptyActions.map((action) => {
                    const isLoading = emptyActionLoading === action.key;
                    const noResult = emptyActionNoResult === action.key;
                    return (
                      <div key={action.key} className="relative">
                        <button
                          type="button"
                          data-testid="chat-empty-action"
                          disabled={!!emptyActionLoading}
                          onClick={() => onEmptyAction?.(action)}
                          className={cn(
                            'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-left text-xs',
                            'border border-border/60 hover:border-border hover:bg-white dark:hover:bg-zinc-800 transition-colors',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                          )}
                        >
                          <span className="shrink-0 flex h-6 w-6 items-center justify-center rounded bg-muted/60">
                            {isLoading ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : action.icon === 'whatsapp' ? (
                              <WhatsAppIcon className="h-3 w-3 text-muted-foreground" />
                            ) : action.icon === 'sms' ? (
                              <MessageSquare className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Search className="h-3 w-3 text-muted-foreground" />
                            )}
                          </span>
                          <div className="flex flex-col min-w-0">
                            <span className="text-muted-foreground font-medium">{action.label}</span>
                            <span className="text-muted-foreground/60 truncate text-[10px]">{action.description}</span>
                          </div>
                        </button>
                        {/* "No conversations" tooltip */}
                        {noResult && (
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-foreground text-background text-[11px] font-medium whitespace-nowrap shadow-lg animate-in fade-in slide-in-from-bottom-1 duration-200">
                            No conversations found
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              {groupedMessages.map((group, gi) => {
                const isLastGroup = gi === groupedMessages.length - 1;
                return (
                <div
                  key={group.date}
                  ref={(el) => {
                    if (el) groupRefs.current.set(group.date, el);
                    else groupRefs.current.delete(group.date);
                  }}
                >
                  <DateSeparator date={group.date} />
                  <div className="space-y-1">
                    {group.messages.map((msg, mi) => (
                      <ChatMessage
                        key={msg.clientId || msg.id}
                        message={msg}
                        previousMessage={mi > 0 ? group.messages[mi - 1] : undefined}
                        onRetry={handleRetry}
                        onPin={handlePin}
                        onStar={handleStar}
                        onReply={setReplyingTo}
                        onReact={handleReact}
                        onCancelScheduled={cancelScheduledMessage}
                        isPinned={conversation.pinnedMessageId === msg.id}
                        isStarred={!!starredMessages[msg.id]}
                        isLastMessage={isLastGroup && mi === group.messages.length - 1}
                        isNew={realtimeMessageIds.has(msg.id)}
                        allMessages={messages}
                      />
                    ))}
                  </div>
                </div>
                );
              })}
              {revalidating && (
                <div className="flex justify-center py-2">
                  <div className="h-4 w-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                </div>
              )}
              <div ref={messagesEndRef} className="h-4" />
            </>
          )}

          {/* Loading indicator when refreshing with existing messages */}
          {loading && messages.length > 0 && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          </div>
        </div>
      </div>

      {/* WhatsApp 131026 — recipient may not be on WhatsApp / blocked business */}
      {conversation.channel === 'WHATSAPP'
        && conversation.metadata?.lastMessageStatus === 'failed'
        && (conversation.metadata?.lastFailedCode as number | undefined) === 131026 && (
        <div className="mx-3 mt-2 flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-px text-amber-600 dark:text-amber-400" />
          <span className="leading-snug">
            <span className="font-semibold">This user may not be on WhatsApp or has blocked your business.</span>
          </span>
        </div>
      )}

      {/* Chat input */}
      <ChatInput
        conversation={conversation}
        onMessageSent={onMessageSent}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        optOutLocked={optOutLocked}
        onOptOutSendAnyway={() => setOptOutOverrideKey(optOutLockKey(conversation))}
        presenceOthers={presenceOthers}
        showPresenceInline={hideHeader}
        onTypingChange={setPresenceTyping}
      />
    </div>
  );
}

function MessagesSkeleton() {
  return (
    <div className="flex flex-col gap-3 py-4">
      {/* Date separator skeleton */}
      <div className="flex items-center gap-3 py-2">
        <div className="flex-1 border-t border-border/30" />
        <Skeleton className="h-4 w-24 rounded-full" />
        <div className="flex-1 border-t border-border/30" />
      </div>

      {/* Inbound — short message */}
      <div className="flex justify-start">
        <div className="space-y-1">
          <Skeleton className="h-9 w-36 rounded-2xl rounded-bl-md" />
          <Skeleton className="h-2.5 w-12" />
        </div>
      </div>

      {/* Outbound — medium message */}
      <div className="flex justify-end">
        <div className="space-y-1">
          <Skeleton className="h-[42px] w-52 rounded-2xl rounded-br-md bg-primary/15" />
          <div className="flex items-center gap-1 justify-end">
            <Skeleton className="h-2.5 w-10 bg-primary/10" />
            <Skeleton className="h-2.5 w-2.5 rounded-full bg-primary/10" />
          </div>
        </div>
      </div>

      {/* Inbound — longer message (2 lines) */}
      <div className="flex justify-start">
        <div className="space-y-1">
          <div className="space-y-0">
            <Skeleton className="h-[52px] w-64 rounded-2xl rounded-bl-md" />
          </div>
          <Skeleton className="h-2.5 w-12" />
        </div>
      </div>

      {/* Outbound — short reply */}
      <div className="flex justify-end">
        <div className="space-y-1">
          <Skeleton className="h-9 w-28 rounded-2xl rounded-br-md bg-primary/15" />
          <div className="flex items-center gap-1 justify-end">
            <Skeleton className="h-2.5 w-10 bg-primary/10" />
            <Skeleton className="h-2.5 w-2.5 rounded-full bg-primary/10" />
          </div>
        </div>
      </div>

      {/* Inbound — image placeholder */}
      <div className="flex justify-start">
        <div className="space-y-1">
          <Skeleton className="h-40 w-52 rounded-2xl rounded-bl-md" />
          <Skeleton className="h-2.5 w-12" />
        </div>
      </div>

      {/* Outbound — medium message */}
      <div className="flex justify-end">
        <div className="space-y-1">
          <Skeleton className="h-[46px] w-48 rounded-2xl rounded-br-md bg-primary/15" />
          <div className="flex items-center gap-1 justify-end">
            <Skeleton className="h-2.5 w-10 bg-primary/10" />
            <Skeleton className="h-2.5 w-2.5 rounded-full bg-primary/10" />
          </div>
        </div>
      </div>

      {/* Another date separator */}
      <div className="flex items-center gap-3 py-2">
        <div className="flex-1 border-t border-border/30" />
        <Skeleton className="h-4 w-20 rounded-full" />
        <div className="flex-1 border-t border-border/30" />
      </div>

      {/* Inbound — short */}
      <div className="flex justify-start">
        <div className="space-y-1">
          <Skeleton className="h-9 w-44 rounded-2xl rounded-bl-md" />
          <Skeleton className="h-2.5 w-12" />
        </div>
      </div>

      {/* Outbound — long message (3 lines) */}
      <div className="flex justify-end">
        <div className="space-y-1">
          <Skeleton className="h-[68px] w-60 rounded-2xl rounded-br-md bg-primary/15" />
          <div className="flex items-center gap-1 justify-end">
            <Skeleton className="h-2.5 w-10 bg-primary/10" />
            <Skeleton className="h-2.5 w-2.5 rounded-full bg-primary/10" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Pinned message banner — renders above the messages area */
function PinnedMessageBanner({
  conversation,
  messages,
  scrollContainerRef,
  onUnpin,
}: {
  conversation: Conversation;
  messages: Message[];
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onUnpin: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Try to find the pinned message in the loaded messages array first
  const pinnedMsgFromList = messages.find((m) => m.id === conversation.pinnedMessageId);

  // Fallback to stored pinnedMessageData from Firestore inbox doc
  const pinnedMessageData = conversation.metadata?.pinnedMessageData as Record<string, unknown> | undefined;
  const pinnedMeta = pinnedMessageData?.pinned as Record<string, unknown> | undefined;

  // Derive display values — prefer loaded message, fallback to stored Firestore data
  const pinnedBody = pinnedMsgFromList?.body
    || (pinnedMessageData?.body as string)
    || (pinnedMessageData?.text as string)
    || '';
  const isOutbound = pinnedMsgFromList
    ? pinnedMsgFromList.direction === 'outbound'
    : (pinnedMessageData?.direction as string)?.startsWith('outbound') ?? false;
  const hasMedia = pinnedMsgFromList
    ? ((pinnedMsgFromList.attachments && pinnedMsgFromList.attachments.length > 0) || !!pinnedMsgFromList.mediaUrl)
    : !!(pinnedMessageData?.num_media && parseInt(String(pinnedMessageData.num_media), 10) > 0);
  const pinnedBy = pinnedMsgFromList?.metadata?.pinnedBy as { name?: string; email?: string } | undefined
    || (pinnedMeta?.createdBy_UA as { name?: string; email?: string } | undefined);
  const pinnedTime = (pinnedMsgFromList?.metadata?.pinnedTime as number | undefined)
    || (pinnedMeta?.createdTime as number | undefined);
  const pinnedAttachments = pinnedMsgFromList?.attachments;

  // If neither source has data, don't render
  if (!pinnedMsgFromList && !pinnedMessageData) return null;

  const pinnedMsgId = conversation.pinnedMessageId;

  // Scroll to the original message in the chat
  const scrollToMessage = () => {
    if (!pinnedMsgId) return;
    const el = document.getElementById(`msg-${pinnedMsgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight flash
      el.classList.add('pinned-highlight');
      setTimeout(() => el.classList.remove('pinned-highlight'), 2000);
    }
  };

  return (
    <div className="border-b border-border bg-[#ccedff]/40 dark:bg-[#1a3a4a]/40 shrink-0">
      <div className="flex items-start gap-2 px-4 py-2">
        {/* Pin icon */}
        <Pin className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 rotate-45 mt-0.5" />

        {/* Content — clickable to scroll to message */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={scrollToMessage}
          role="button"
          tabIndex={0}
          title="Click to scroll to message"
        >
          {/* Author + direction + time */}
          <div className="flex items-center gap-1.5 text-[11px] text-[#027d7d] dark:text-teal-400">
            {isOutbound ? (
              <span className="text-[10px] opacity-60" title="Outgoing">&#8599;</span>
            ) : (
              <span className="text-[10px] opacity-60" title="Incoming">&#8601;</span>
            )}
            {pinnedBy?.name ? (
              <span className="font-semibold">~{pinnedBy.name}</span>
            ) : pinnedBy?.email ? (
              <span className="font-semibold">~{pinnedBy.email.split('@')[0]}</span>
            ) : pinnedMsgFromList?.noteAuthor?.name ? (
              <span className="font-semibold">~{pinnedMsgFromList.noteAuthor.name}</span>
            ) : null}
            {pinnedTime && (
              <span className="text-muted-foreground/60 text-[10px]" title={new Date(pinnedTime).toLocaleString()}>
                {formatPinnedTime(pinnedTime)}
              </span>
            )}
          </div>

          {/* Message body */}
          <p className={cn(
            'text-xs text-foreground mt-0.5 break-words',
            !expanded && 'line-clamp-2'
          )}>
            {pinnedBody || (hasMedia ? 'Media attachment' : 'Pinned message')}
          </p>

          {/* Media indicator (collapsed) */}
          {hasMedia && !expanded && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              <span>Attachment</span>
            </div>
          )}

          {/* Media preview (expanded) */}
          {hasMedia && expanded && pinnedAttachments && pinnedAttachments.length > 0 && (
            <div className="mt-1.5 flex gap-1.5 overflow-x-auto">
              {pinnedAttachments.filter(a => a.url).map((att, i) => (
                att.type === 'image' ? (
                  <img
                    key={i}
                    src={att.url}
                    alt="Pinned media"
                    className="h-16 w-16 rounded-md object-cover border border-border/50"
                  />
                ) : (
                  <div key={i} className="h-16 w-16 rounded-md bg-muted flex items-center justify-center border border-border/50">
                    <FileText className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                )
              ))}
            </div>
          )}
        </div>

        {/* Actions: expand/collapse + unpin */}
        <div className="flex items-center gap-1 shrink-0 mt-0.5">
          {(pinnedBody && pinnedBody.length > 100 || hasMedia) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-muted-foreground/60 hover:text-foreground transition-colors p-0.5"
              title={expanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnpin();
            }}
            className="text-muted-foreground/50 hover:text-red-500 transition-colors p-0.5"
            title="Unpin message"
          >
            <PinOff className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Format pinned timestamp as short relative string */
function formatPinnedTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a Date as the `YYYY-MM-DDTHH:mm` string a datetime-local input expects.
 *
 * Must be built from LOCAL parts. `toISOString()` returns UTC, and the input
 * reads whatever it is given as local time — so an ISO string silently shifts
 * the reminder by the user's UTC offset (IST users lost 5:30, which made every
 * preset land in the past). `new Date('YYYY-MM-DDTHH:mm')` on the way back out
 * parses as local, so this is the only conversion that needs care.
 */
function toDateTimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Module-level so click handlers can check the clock without tripping react-hooks/purity. */
function isInPast(epochMs: number): boolean {
  return epochMs <= Date.now();
}

/** Due inside the next hour — same reason as isInPast: keep the clock out of render. */
function isDueSoon(epochMs: number): boolean {
  return epochMs > 0 && !isInPast(epochMs) && epochMs - Date.now() < 60 * 60 * 1000;
}

/** Inline reminder form — shown in the chat header area */
/** Compute quick time presets */
function getReminderPresets() {
  const now = new Date();
  // In 1 hour
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  // Tomorrow 9 AM
  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);
  // Next Monday 9 AM
  const nextMon = new Date(now);
  const dayOfWeek = nextMon.getDay();
  const daysUntilMon = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : 8 - dayOfWeek;
  nextMon.setDate(nextMon.getDate() + daysUntilMon);
  nextMon.setHours(9, 0, 0, 0);

  const fmt = (d: Date) => {
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day} ${time}`;
  };

  return [
    { label: 'In 1 hour', icon: Sun, time: inOneHour, hint: fmt(inOneHour) },
    { label: 'Tomorrow', icon: ChevronRight, time: tomorrow9, hint: fmt(tomorrow9) },
    { label: 'Next week', icon: ChevronsRight, time: nextMon, hint: fmt(nextMon) },
  ];
}

function SetReminderForm({
  conversationId,
  contactPhone,
  conversation,
  onClose,
}: {
  conversationId: string;
  contactPhone: string;
  conversation: Conversation;
  onClose: () => void;
}) {
  const [step, setStep] = useState<'presets' | 'form'>('presets');
  const [subject, setSubject] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  // The task being rescheduled. null = creating a NEW reminder — several can
  // coexist on one chat (matches legacy: the badge is just the "next" one).
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [addingAnother, setAddingAnother] = useState(false);
  const setScheduledEvent = useConversationStore((s) => s.setScheduledEvent);
  const cancelOrCompleteReminder = useConversationStore((s) => s.cancelOrCompleteReminder);
  const chatReminderRows = useConversationStore((s) => s.chatReminderRows);
  const chatReminderConvId = useConversationStore((s) => s.chatReminderConvId);
  const chatRemindersLoading = useConversationStore((s) => s.chatRemindersLoading);
  const chatRemindersFailed = useConversationStore((s) => s.chatRemindersFailed);
  const subjectRef = useRef<HTMLInputElement>(null);

  // This chat's own reminders — ChatThread, this form's parent, fetches them when
  // the conversation opens.
  const convReminders = useMemo(
    () => (chatReminderConvId === conversationId ? chatReminderRows : []),
    [chatReminderRows, chatReminderConvId, conversationId]
  );
  // Trust the badge only WHILE the drawer is answering. Once it has answered,
  // the drawer is the truth: a stale badge (e.g. a fired-but-unread reminder's
  // leftover pointer) must not conjure a phantom "active" row.
  const hasExisting = convReminders.length > 0 ||
    ((chatRemindersLoading || chatRemindersFailed) &&
      !!(conversation.metadata?.hasReminder && conversation.metadata?.reminderTime));

  // The reminder being edited can disappear underneath the form — cancelled here,
  // or closed on another device. Saving would then re-file it.
  useEffect(() => {
    if (editingEventId && !convReminders.some((r) => r.eventId === editingEventId)) {
      setEditingEventId(null);
    }
  }, [convReminders, editingEventId]);

  const presets = useMemo(() => getReminderPresets(), []);

  const handlePresetClick = (time: Date) => {
    setDateTime(toDateTimeLocalValue(time));
    setStep('form');
    setTimeout(() => subjectRef.current?.focus(), 50);
  };

  const handleCustomClick = () => {
    setDateTime(toDateTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)));
    setStep('form');
    setTimeout(() => subjectRef.current?.focus(), 50);
  };

  /**
   * Open the form seeded with the reminder that already exists, so rescheduling
   * adjusts it rather than starting from a blank subject and an arbitrary time.
   * handleSave passes the existing eventId along, so the server updates the same
   * task instead of filing a second one.
   */
  const handleReschedule = (item: { eventId: string; subject: string; performAt: number }) => {
    setSubject(item.subject || '');
    setEditingEventId(item.eventId);
    // An OVERDUE reminder's own time is in the past — prefilling it would let
    // one click save a reminder that can never usefully fire. Seed with a
    // future time instead; a still-upcoming reminder keeps its own time.
    if (!item.performAt || isInPast(item.performAt)) {
      handleCustomClick();
      return;
    }
    setDateTime(toDateTimeLocalValue(new Date(item.performAt)));
    setStep('form');
    setTimeout(() => subjectRef.current?.focus(), 50);
  };

  const handleSave = async () => {
    if (!dateTime) return;
    // Legacy blocks past times too ("Invalid date and time.") — a reminder in
    // the past either fires instantly or never, both of which read as broken.
    if (isInPast(new Date(dateTime).getTime())) {
      toast.error('Pick a time in the future', {
        description: 'The reminder time has already passed.',
      });
      return;
    }
    setSaving(true);
    const contactId = contactPhone?.replace(/\D/g, '') || '';
    // editingEventId set = reschedule that task in place; null = a NEW
    // reminder, even when others already exist on this chat.
    const ok = await setScheduledEvent(conversationId, contactId, {
      subject: subject || 'Reminder',
      performAt: new Date(dateTime).getTime(),
      eventId: editingEventId || undefined,
    });
    setSaving(false);
    // Closing on failure would look exactly like success while nothing was
    // saved — keep the form up with what they typed still in it.
    if (!ok) {
      toast.error('Could not set the reminder', { description: 'Nothing was saved — please try again.' });
      return;
    }
    onClose();
  };

  const handleCancelItem = async (eventId: string) => {
    setCancellingId(eventId);
    const contactId = contactPhone?.replace(/\D/g, '') || '';
    const ok = await cancelOrCompleteReminder(conversationId, contactId, 'cancelled', eventId);
    setCancellingId(null);
    if (!ok) toast.error('Could not cancel the reminder', { description: 'It is still scheduled — please try again.' });
    // Stay open: the list re-renders without the cancelled one; when it was
    // the last, the presets picker shows.
  };

  // Existing reminders → list them ALL (several can coexist on one chat).
  // "Add another" drops into the same presets picker with editingEventId null.
  if (hasExisting && step === 'presets' && !addingAnother) {
    // Drawer still loading and only the badge knows: synthesize a one-row list.
    const rows = convReminders.length > 0
      ? convReminders
      : [{
          eventId: (conversation.metadata?.reminderEventId as string) || '',
          subject: (conversation.metadata?.reminderSubject as string) || 'Reminder',
          performAt: (conversation.metadata?.reminderTime as number) || 0,
        }];
    return (
      <div data-testid="reminder-panel" className="border-b border-border bg-amber-50/50 dark:bg-amber-950/20 shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Timer className={cn('h-4 w-4', rows.some((r) => isInPast(r.performAt)) ? 'text-red-500' : 'text-amber-500')} />
              <span className="text-xs font-semibold text-foreground">
                Active Reminder{rows.length > 1 ? `s (${rows.length})` : ''}
              </span>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {rows.map((r) => {
              const overdue = isInPast(r.performAt);
              const dueSoon = isDueSoon(r.performAt);
              return (
                // Each reminder is its own card, divided by a status color bar:
                // red = overdue, amber = due within the hour, blue = upcoming —
                // the same language the bell dialog speaks.
                <div
                  key={r.eventId || r.subject}
                  data-testid="reminder-row"
                  className={cn(
                    'rounded-lg border border-border/60 bg-background px-3 py-2 border-l-[3px]',
                    overdue ? 'border-l-red-500' : dueSoon ? 'border-l-amber-500' : 'border-l-blue-400 dark:border-l-blue-500'
                  )}
                >
                  <p className="text-sm font-medium text-foreground">{r.subject || 'Reminder'}</p>
                  <p className={cn('text-xs', overdue ? 'text-red-500 font-medium' : dueSoon ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground')}>
                    {overdue ? 'Overdue \u00b7 ' : 'Due '}
                    {r.performAt ? new Date(r.performAt).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                    }) : ''}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      data-testid="reminder-reschedule"
                      onClick={() => handleReschedule(r)}
                      // Without an eventId the save mints a NEW task instead of
                      // updating this one, leaving the chat with two reminders.
                      // The fallback row (drawer still loading) has no id.
                      disabled={!r.eventId}
                      title={r.eventId ? undefined : 'Still loading this reminder'}
                      className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 bg-amber-100 dark:bg-amber-900/30 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reschedule
                    </button>
                    <button
                      data-testid="reminder-cancel"
                      onClick={() => handleCancelItem(r.eventId)}
                      disabled={cancellingId === r.eventId || !r.eventId}
                      className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 bg-red-50 dark:bg-red-950/30 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                    >
                      {cancellingId === r.eventId ? 'Cancelling...' : 'Cancel Reminder'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2.5 pl-6">
            <button
              data-testid="reminder-add-another"
              onClick={() => { setAddingAnother(true); setEditingEventId(null); setSubject(''); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
            >
              <Bell className="h-3 w-3" /> Add another reminder
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show time presets picker
  if (step === 'presets') {
    return (
      <div className="border-b border-border bg-amber-50/50 dark:bg-amber-950/20 shrink-0">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-foreground">Set Follow-up Reminder</span>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-0.5">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => handlePresetClick(p.time)}
                className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-amber-100/60 dark:hover:bg-amber-900/20 transition-colors text-left"
              >
                <p.icon className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-xs font-medium text-foreground flex-1">{p.label}</span>
                <span className="text-[11px] text-muted-foreground">{p.hint}</span>
              </button>
            ))}
            <button
              onClick={handleCustomClick}
              className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-amber-100/60 dark:hover:bg-amber-900/20 transition-colors text-left"
            >
              <CalendarClock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-xs font-medium text-foreground flex-1">Pick date & time</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show subject + datetime form
  return (
    <div className="border-b border-border bg-amber-50/50 dark:bg-amber-950/20 shrink-0">
      <div className="px-4 py-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-foreground">
              {editingEventId ? 'Update Reminder' : 'Set Reminder'}
            </span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-2 pl-1">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Subject</label>
            <input
              ref={subjectRef}
              type="text"
              placeholder="Follow-up reminder..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full mt-0.5 rounded-md border border-amber-200 dark:border-amber-800 bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 text-foreground placeholder:text-muted-foreground/50"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Clock className="h-3 w-3" /> Remind At
            </label>
            <input
              type="datetime-local"
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className="w-full mt-0.5 rounded-md border border-amber-200 dark:border-amber-800 bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 text-foreground"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            data-testid="reminder-save"
            onClick={handleSave}
            disabled={saving || !dateTime}
            className="text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 px-4 py-1.5 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : editingEventId ? 'Update Reminder' : 'Set Reminder'}
          </button>
          <button
            // Back cancels the whole intent. Resetting only `step` left
            // `addingAnother` set — which renders the picker again instead of the
            // reminder list, with no way back — and left `editingEventId` pointing
            // at a reminder that may since have been cancelled, so the next save
            // re-filed it.
            onClick={() => { setStep('presets'); setAddingAnother(false); setEditingEventId(null); setSubject(''); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}

/** Call button — dropdown with Voice Call / WhatsApp Call options + set-default.
 *  When a default is set, clicking the button directly initiates that call type.
 *  The chevron always opens the dropdown to choose or change the default.
 */
function CallButton({ phone, channel }: { phone: string; channel?: Channel }) {
  // ── Hooks (all before any conditional return) ──
  const extensionName = useAppStore((s) => s.extensionName);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);
  const softphoneState = useCallStore((s) => s.softphoneState);
  const setDialerNumber = useCallStore((s) => s.setDialerNumber);
  const setSelectedCallerId = useCallStore((s) => s.setSelectedCallerId);
  // KAN-178: already filtered by Teams & Permissions (caller-id visibility + hidden senders)
  const callerIds = useVisibleCallerIds();
  const makeCall = useCallStore((s) => s.makeCall);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const setCallManagerTab = useCallStore((s) => s.setCallManagerTab);
  const whatsAppCallPermissions = useCallStore((s) => s.whatsAppCallPermissions);
  const requestWhatsAppCallPermission = useCallStore((s) => s.requestWhatsAppCallPermission);

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [defaultMode, setDefaultModeState] = useState<'pstn' | 'whatsapp' | null>(() => {
    const v = localStorage.getItem('ulgebra-default-call-mode');
    return v === 'pstn' || v === 'whatsapp' ? v : null;
  });

  // Close dropdown on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // ── Early return ──
  const isTwilioExt = extensionName?.startsWith('twilio') ?? false;
  if (!isTwilioExt || !enableVoiceCalling) return null;

  // ── Derived state ──
  const isReady = softphoneState === 'registered';
  const trimmedPhone = phone?.trim() || '';
  const hasWhatsAppCid = callerIds.some((c) => c.channel === 'WHATSAPP');
  const hasPstnCid = callerIds.some((c) => c.channel !== 'WHATSAPP');
  const waPermission = trimmedPhone ? (whatsAppCallPermissions[trimmedPhone] || 'none') : 'none';

  const setDefaultMode = (mode: 'pstn' | 'whatsapp' | null) => {
    setDefaultModeState(mode);
    if (mode) localStorage.setItem('ulgebra-default-call-mode', mode);
    else localStorage.removeItem('ulgebra-default-call-mode');
  };

  const autoSelectCallerId = (mode: 'pstn' | 'whatsapp') => {
    if (mode === 'whatsapp') {
      const waCid = callerIds.find((c) => c.channel === 'WHATSAPP');
      if (waCid) setSelectedCallerId(waCid.value);
    } else {
      const pstnCid = callerIds.find((c) => c.channel !== 'WHATSAPP');
      if (pstnCid) setSelectedCallerId(pstnCid.value);
    }
  };

  // ── Call handlers ──
  const handlePstnCall = () => {
    if (trimmedPhone) setDialerNumber(trimmedPhone);
    autoSelectCallerId('pstn');
    setCallManagerTab('dialer');
    toggleCallManager();
    setShowMenu(false);
  };

  const handleWhatsAppAction = () => {
    setShowMenu(false);
    autoSelectCallerId('whatsapp');
    if (waPermission === 'accepted') {
      if (trimmedPhone) setDialerNumber(trimmedPhone);
      makeCall(trimmedPhone).catch(() => {
        setCallManagerTab('dialer');
        toggleCallManager();
      });
    } else if (waPermission === 'none') {
      requestWhatsAppCallPermission(trimmedPhone);
    } else {
      // pending or rejected — open dialer to show status
      if (trimmedPhone) setDialerNumber(trimmedPhone);
      setCallManagerTab('dialer');
      toggleCallManager();
    }
  };

  // Main button click — use default or open dropdown
  const handleMainClick = () => {
    if (defaultMode === 'pstn' && hasPstnCid) {
      handlePstnCall();
    } else if (defaultMode === 'whatsapp' && hasWhatsAppCid) {
      handleWhatsAppAction();
    } else {
      setShowMenu((v) => !v);
    }
  };

  // ── Render helpers ──
  const ButtonIcon = defaultMode === 'whatsapp' ? MessageCircle : Phone;
  const buttonColor = defaultMode === 'whatsapp'
    ? 'hover:text-green-600 hover:bg-green-500/10'
    : 'hover:text-emerald-600 hover:bg-emerald-500/10';

  // WhatsApp option label based on permission state
  const waLabel =
    waPermission === 'none' ? 'Send Call Request' :
    waPermission === 'pending' ? 'Awaiting Permission…' :
    waPermission === 'rejected' ? 'Permission Denied' :
    'WhatsApp Call';

  return (
    <div className="relative" data-testid="chat-action-call" ref={menuRef}>
      <div className="flex items-center">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleMainClick}
          disabled={!isReady}
          className={cn('text-muted-foreground', buttonColor)}
          title={
            !isReady ? 'Voice not ready'
            : defaultMode === 'whatsapp' ? 'WhatsApp Call'
            : defaultMode === 'pstn' ? 'Voice Call'
            : 'Call'
          }
          data-call-manager-trigger
        >
          <ButtonIcon className="h-3.5 w-3.5" />
        </Button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
          disabled={!isReady}
          className="flex items-center justify-center h-5 w-4 -ml-1.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors disabled:opacity-30"
          title="Call options"
        >
          <ChevronDown className="h-2.5 w-2.5" />
        </button>
      </div>

      {/* Dropdown */}
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-popover shadow-lg z-50 py-1">
          {/* Voice Call */}
          {hasPstnCid && (
            <button
              onClick={handlePstnCall}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors"
            >
              <Phone className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span className="flex-1 text-left text-foreground">Voice Call</span>
              {defaultMode === 'pstn' && (
                <span className="text-[10px] text-primary font-medium bg-primary/10 rounded-full px-1.5 py-0.5">
                  Default
                </span>
              )}
            </button>
          )}

          {/* WhatsApp Call */}
          {hasWhatsAppCid && (
            <button
              onClick={handleWhatsAppAction}
              disabled={waPermission === 'pending'}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors',
                waPermission === 'pending' && 'opacity-50 cursor-not-allowed'
              )}
            >
              <MessageCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
              <span className="flex-1 text-left text-foreground">{waLabel}</span>
              {waPermission === 'pending' && (
                <Loader2 className="h-3 w-3 animate-spin text-amber-500 shrink-0" />
              )}
              {defaultMode === 'whatsapp' && (
                <span className="text-[10px] text-primary font-medium bg-primary/10 rounded-full px-1.5 py-0.5">
                  Default
                </span>
              )}
            </button>
          )}

          {/* Divider + Default setting */}
          <div className="my-1 border-t border-border" />
          <div className="px-3 py-1.5">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-1.5">
              Default call mode
            </p>
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="callDefault"
                  checked={defaultMode === null}
                  onChange={() => setDefaultMode(null)}
                  className="h-3 w-3 accent-primary"
                />
                <span className="text-xs text-foreground group-hover:text-primary transition-colors">
                  Always ask
                </span>
              </label>
              {hasPstnCid && (
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="callDefault"
                    checked={defaultMode === 'pstn'}
                    onChange={() => setDefaultMode('pstn')}
                    className="h-3 w-3 accent-primary"
                  />
                  <span className="text-xs text-foreground group-hover:text-primary transition-colors">
                    Voice Call
                  </span>
                </label>
              )}
              {hasWhatsAppCid && (
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="callDefault"
                    checked={defaultMode === 'whatsapp'}
                    onChange={() => setDefaultMode('whatsapp')}
                    className="h-3 w-3 accent-primary"
                  />
                  <span className="text-xs text-foreground group-hover:text-primary transition-colors">
                    WhatsApp Call
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * WhatsApp native calling button — uses Meta's WhatsApp Calling API directly.
 * Separate from the Twilio-based CallButton above.
 */
function WhatsAppNativeCallButton({ phone, channel, senderMsisdn }: { phone: string; channel?: Channel; senderMsisdn?: string }) {
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [showCallSettings, setShowCallSettings] = useState(false);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const extensionName = useAppStore((s) => s.extensionName);
  const activeCall = useWhatsAppStore((s) => s.activeWhatsAppCall);
  const callingEnabled = useWhatsAppStore((s) => s.callingEnabled);
  const whatsappNumberVsIdMap = useWhatsAppStore((s) => s.whatsappNumberVsIdMap);
  const addToast = useUIStore((s) => s.addToast);
  const activeConversation = useConversationStore((s) => s.activeConversation);

  // Only show for WhatsApp extensions
  const isWhatsAppExt = extensionName?.includes('whatsapp') ?? false;
  if (!isWhatsAppExt || !channel || channel !== 'WHATSAPP') return null;

  // Don't show if there's already an active call
  if (activeCall) return null;

  // Resolve the phone number ID from the conversation's sender MSISDN
  const senderPhoneNumberId = senderMsisdn ? whatsappNumberVsIdMap[senderMsisdn.replace(/\D/g, '')] : undefined;

  // Read call permission from conversation metadata (raw Facebook payload)
  const callPermission = activeConversation?.metadata?.callPermission as {
    response?: string; is_permanent?: boolean; expiration_timestamp?: string; response_source?: string;
  } | undefined;

  const permissionAccepted = callPermission?.response === 'accept';
  const expirationMs = callPermission?.expiration_timestamp ? parseInt(callPermission.expiration_timestamp, 10) * 1000 : undefined;
  const permissionExpired = permissionAccepted && !callPermission?.is_permanent && expirationMs
    ? expirationMs < Date.now()
    : false;
  const hasValidPermission = permissionAccepted && !permissionExpired;

  const getWaModule = () => tpaModule as import('@/modules/tpa/whatsapp/whatsapp.module').WhatsAppModule;

  /** Check (or fetch) call settings and return whether calling is enabled */
  const ensureCallSettingsChecked = async (): Promise<boolean> => {
    if (!tpaModule || !senderPhoneNumberId) {
      addToast({ title: 'Call unavailable', description: 'Could not resolve sender phone number.', type: 'error' });
      return false;
    }

    // Already know it's enabled
    if (callingEnabled[senderPhoneNumberId]) return true;

    // Not yet fetched or disabled — fetch fresh settings
    setChecking(true);
    try {
      const settings = await getWaModule().getCallSettings(senderPhoneNumberId);
      const enabled = settings?.status === 'ENABLED';

      if (!enabled) {
        setShowCallSettings(true);
        return false;
      }
      return true;
    } catch {
      addToast({ title: 'Call settings check failed', description: 'Could not verify calling status. Try again.', type: 'error' });
      return false;
    } finally {
      setChecking(false);
    }
  };

  const handleClick = async () => {
    const enabled = await ensureCallSettingsChecked();
    if (enabled) {
      setOpen(true);
    }
  };

  const handleCall = () => {
    setOpen(false);
    const trimmed = phone?.trim();
    if (!trimmed || !tpaModule) return;
    getWaModule().initiateCall(trimmed, senderPhoneNumberId);
  };

  const handleRequestPermission = async () => {
    const trimmed = phone?.trim();
    if (!trimmed || !tpaModule || !senderPhoneNumberId) return;
    setRequesting(true);
    try {
      const sent = await getWaModule().sendCallPermissionRequest(trimmed, senderPhoneNumberId);
      if (sent) {
        addToast({ title: 'Permission requested', description: 'Call permission request sent to the recipient.', type: 'success' });
      } else {
        addToast({ title: 'Request failed', description: 'Failed to send call permission request.', type: 'error' });
      }
    } catch (err) {
      console.error('[WhatsAppCall] Failed to send call permission request:', err);
      addToast({ title: 'Request failed', description: 'Failed to send call permission request.', type: 'error' });
    } finally {
      setRequesting(false);
      setOpen(false);
    }
  };

  // Permission status label for the popover
  const renderPermissionStatus = () => {
    if (!callPermission) return null;

    if (callPermission.response === 'reject') {
      return (
        <div className="px-2.5 py-2 text-xs text-red-500 border-b border-border mb-1">
          Permission rejected{callPermission.response_source === 'automatic' ? ' (auto)' : ''}
        </div>
      );
    }

    if (permissionExpired) {
      return (
        <div className="px-2.5 py-2 text-xs text-amber-500 border-b border-border mb-1">
          Permission expired
        </div>
      );
    }

    if (hasValidPermission) {
      const permLabel = callPermission.is_permanent ? 'Permanent' : 'Temporary';
      const expiry = !callPermission.is_permanent && expirationMs
        ? ` · Expires ${new Date(expirationMs).toLocaleDateString()}`
        : '';
      return (
        <div className="px-2.5 py-2 text-xs text-emerald-600 border-b border-border mb-1">
          {permLabel} permission granted{expiry}
        </div>
      );
    }

    return null;
  };

  return (
    <>
    <Popover open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleClick}
          disabled={checking}
          className={cn(
            'text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10',
            hasValidPermission && 'text-emerald-600',
          )}
          title={hasValidPermission ? 'WhatsApp Voice Call (permission granted)' : 'WhatsApp Voice Call'}
        >
          {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
        </Button>
      </PopoverTrigger>
      {open && (
        <PopoverContent align="end" className="w-56 p-1">
          {renderPermissionStatus()}
          {(!hasValidPermission) && (
            <button
              onClick={handleRequestPermission}
              disabled={requesting}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              {requesting ? 'Sending...' : 'Request Call Permission'}
            </button>
          )}
          <button
            onClick={handleCall}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground hover:bg-accent transition-colors',
              !hasValidPermission && 'opacity-60',
            )}
          >
            <PhoneOutgoing className="h-3.5 w-3.5 text-emerald-600" />
            Call Now
          </button>
        </PopoverContent>
      )}
    </Popover>

      {/* Call settings dialog — shown when calling is not enabled */}
      {senderPhoneNumberId && (
        <WhatsAppCallSettingsDialog
          open={showCallSettings}
          onOpenChange={setShowCallSettings}
          senderPhoneNumberId={senderPhoneNumberId}
          onEnabled={() => {
            // Re-trigger the call flow after enabling
            handleClick();
          }}
        />
      )}
    </>
  );
}

function LookupToggleButton() {
  const saasModule = useModuleStore((s) => s.saasModule);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const lookupPanelOpen = useUIStore((s) => s.lookupPanelOpen);
  const toggleLookupPanel = useUIStore((s) => s.toggleLookupPanel);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);

  // Hide in CRM record context (lookup is redundant when already inside the CRM)
  if (crmRecordContext) return null;

  // Only show the lookup toggle if the current SaaS has a Lookup app configured
  if (!saasModule || !appCodeSAAS || !LOOKUP_SUPPORTED_SAAS.has(appCodeSAAS)) return null;

  // When the lookup panel is already open, hide the header toggle — the panel
  // shows the SaaS logo in its own header and has its own hide button, so the
  // duplicate logo in the chat header is just noise.
  if (lookupPanelOpen) return null;

  return (
    <button
      type="button"
      onClick={toggleLookupPanel}
      className={cn(
        'rounded transition-colors',
        lookupPanelOpen ? 'opacity-100' : 'opacity-60 hover:opacity-100'
      )}
      title={lookupPanelOpen ? 'Close CRM lookup' : 'Open CRM lookup'}
    >
      {saasModule.icon ? (
        <img
          src={saasModule.icon}
          alt={saasModule.name}
          className="h-5 w-5 object-contain"
          onError={(e) => {
            // Replace broken image with initial letter
            const span = document.createElement('span');
            span.className = 'h-5 w-5 rounded bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary';
            span.textContent = saasModule.name.charAt(0);
            (e.target as HTMLImageElement).replaceWith(span);
          }}
        />
      ) : (
        <Database className="h-5 w-5" />
      )}
    </button>
  );
}

/** Full chat skeleton — shown when a conversation is selected but everything is still loading */
export function ChatBoxSkeleton() {
  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Header skeleton */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3 bg-card shadow-sm shrink-0">
        {/* Avatar */}
        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
        {/* Contact info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-12 rounded-full" />
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex items-center gap-1">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>

      {/* Messages area skeleton */}
      <div className="flex-1 min-h-0 overflow-hidden px-4 py-4">
        <MessagesSkeleton />
      </div>

      {/* Input area skeleton */}
      <div className="border-t border-border bg-card/50 p-3 shrink-0">
        <div className="flex items-end gap-2">
          <Skeleton className="h-8 w-8 rounded-lg shrink-0 mb-0.5" />
          <Skeleton className="h-8 w-8 rounded-lg shrink-0 mb-0.5" />
          <Skeleton className="h-8 w-8 rounded-lg shrink-0 mb-0.5" />
          <Skeleton className="h-[36px] flex-1 rounded-xl" />
          <Skeleton className="h-9 w-9 rounded-md shrink-0 mb-0.5" />
        </div>
      </div>
    </div>
  );
}
